import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Building,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Save,
  Share2,
} from 'lucide-react';
import { ENV } from '../../config/env';
import {
  useUpdateWebsiteContact,
  useWebsiteContact,
} from '../../services/httpServices/queries';
import type { WebsiteContactSettings } from '../../services/httpServices/websiteService';

const websiteUrl = ENV.VITE_WEBSITE_URL;

const emptyForm: WebsiteContactSettings = {
  phone: '',
  phoneAlt: '',
  email: '',
  address: '',
  workingHours: '',
  mapEmbedUrl: '',
  mapLinkUrl: '',
  recipientEmail: '',
  isActive: true,
};

const inputBase =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
const labelClass = 'block text-sm font-medium text-text mb-1';

export function WebsiteContact() {
  const { data, isLoading, error } = useWebsiteContact();
  const updateMutation = useUpdateWebsiteContact();

  const [form, setForm] = useState<WebsiteContactSettings>(emptyForm);

  useEffect(() => {
    if (data?.data) {
      setForm({ ...emptyForm, ...data.data });
    }
  }, [data]);

  const updateField = <K extends keyof WebsiteContactSettings>(
    field: K,
    value: WebsiteContactSettings[K],
  ) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // Never send the row id — the update DTO only whitelists editable fields.
    const { id: _id, ...payload } = form;
    try {
      // Success/error feedback is handled by the global mutation cache.
      await updateMutation.mutateAsync(payload);
    } catch {
      // Error toast is shown by the global MutationCache.
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load website contact settings.</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Phone className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Contact Page</h1>
            <p className="text-sm text-text-muted">
              Map, secondary number and contact-form mailbox. Phone, email and address come from
              General Settings.
            </p>
          </div>
        </div>
        <a
          href={`${websiteUrl}/contact`}
          target="_blank"
          rel="noreferrer"
          className="px-4 py-2 rounded-lg border border-border text-sm text-text hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
        >
          <ExternalLink size={16} />
          View Contact Page
        </a>
      </div>

      <form onSubmit={handleSave} className="bg-surface border border-border rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Share2 size={18} className="text-primary" />
          <h2 className="font-semibold text-text">Contact Details</h2>
        </div>

        {/* Mobile Number, Email and Address live in General Settings. */}
        <div className="flex items-start gap-2 rounded-lg border border-border bg-background p-3 text-sm text-text-muted">
          <Building size={16} className="mt-0.5 shrink-0 text-primary" />
          <div>
            <p>
              <span className="font-medium text-text">Mobile Number, Email</span> and{' '}
              <span className="font-medium text-text">Address</span> are managed in{' '}
              <Link to="/settings/general" className="font-medium text-primary hover:underline">
                General Settings
              </Link>{' '}
              and shown on the website automatically — no need to enter them here.
            </p>
            <p className="mt-1 text-xs">
              Hotline: {form.phone || '—'} · Email: {form.email || '—'} · Address:{' '}
              {form.address || '—'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Secondary Number</label>
            <input
              type="text"
              className={inputBase}
              value={form.phoneAlt}
              onChange={(e) => updateField('phoneAlt', e.target.value)}
              placeholder="+880 1810 000001"
            />
          </div>
          <div>
            <label className={labelClass}>Working Hours</label>
            <input
              type="text"
              className={inputBase}
              value={form.workingHours}
              onChange={(e) => updateField('workingHours', e.target.value)}
              placeholder="Mon - Fri 8:00 - 6:30"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-border space-y-4">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-primary" />
            <h2 className="font-semibold text-text">Map</h2>
          </div>
          <div>
            <label className={labelClass}>Google Maps Embed URL *</label>
            <textarea
              className={`${inputBase} font-mono text-xs`}
              rows={3}
              value={form.mapEmbedUrl}
              onChange={(e) => updateField('mapEmbedUrl', e.target.value)}
              placeholder="https://www.google.com/maps/embed?pb=..."
            />
            <p className="text-xs text-text-muted mt-1">
              In Google Maps choose <strong>Share → Embed a map</strong> and paste the{' '}
              <code>src</code> value (the part inside <code>src=&quot;...&quot;</code>).
            </p>
          </div>
          <div>
            <label className={labelClass}>Open-in-Maps Link</label>
            <input
              type="text"
              className={inputBase}
              value={form.mapLinkUrl}
              onChange={(e) => updateField('mapLinkUrl', e.target.value)}
              placeholder="https://maps.app.goo.gl/..."
            />
          </div>
        </div>

        <div className="pt-4 border-t border-border space-y-4">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-primary" />
            <h2 className="font-semibold text-text">Contact Form Mail</h2>
          </div>
          <div>
            <label className={labelClass}>Send contact messages to *</label>
            <input
              type="text"
              className={inputBase}
              value={form.recipientEmail}
              onChange={(e) => updateField('recipientEmail', e.target.value)}
              placeholder="info@firstaid-hospital.com, reception@firstaid-hospital.com"
            />
            <p className="text-xs text-text-muted mt-1">
              One or more email addresses, separated by commas. Every message submitted through the
              website contact form is emailed here.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => updateField('isActive', e.target.checked)}
              className="rounded border-border"
            />
            Contact details active
          </label>
        </div>

        <div className="flex items-center gap-2 pt-4 border-t border-border">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
}
